import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosInstance } from 'axios'
import { publicApiClient } from '../api/client'
import { Album, CreateAlbumInput, UpdateAlbumInput } from '../types/album'

export const useAlbums = (
  apiClient: AxiosInstance = publicApiClient,
  params?: { sortBy?: 'name' | 'createdAt'; sortOrder?: 'asc' | 'desc' },
  enabled = true
) => {
  // Include API client type in query key to separate public/admin caches
  const clientType = apiClient === publicApiClient ? 'public' : 'admin'

  return useQuery({
    queryKey: ['albums', clientType, params],
    queryFn: async () => {
      const { data } = await apiClient.get('/albums', {
        params: {
          sortBy: params?.sortBy || 'name',
          sortOrder: params?.sortOrder || 'asc',
        }
      })
      const response = data.data as { items: Album[]; nextCursor?: string }
      return response.items
    },
    enabled,
  })
}

export const useAlbum = (id: string, apiClient: AxiosInstance = publicApiClient) => {
  // Include API client type in query key to separate public/admin caches
  const clientType = apiClient === publicApiClient ? 'public' : 'admin'
  
  return useQuery({
    queryKey: ['album', clientType, id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/albums/${id}`)
      return data.data as Album
    },
    enabled: !!id,
  })
}

export const useCreateAlbum = (apiClient: AxiosInstance) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateAlbumInput) => {
      const { data } = await apiClient.post('/albums', input)
      return data.data as Album
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['albums'] })
    },
  })
}

export const useUpdateAlbum = (apiClient: AxiosInstance) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateAlbumInput }) => {
      const { data } = await apiClient.patch(`/albums/${id}`, input)
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['albums'] })
      queryClient.invalidateQueries({ queryKey: ['album', variables.id] })
    },
  })
}

export const useDeleteAlbum = (apiClient: AxiosInstance) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/albums/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['albums'] })
    },
  })
}